import * as React from "react";

export interface BadgeOrigin {
  vertical: "top" | "bottom";
  horizontal: "left" | "right";
}

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  anchorOrigin?: BadgeOrigin;
  overlap?: "rectangle" | "circle" | "rectangular" | "circular";
  badgeContent?: React.ReactNode;
  children?: React.ReactNode;
  color?: "primary" | "secondary" | "default" | "error";
  invisible?: boolean;
  max?: number;
  showZero?: boolean;
  variant?: "standard" | "dot";
}

export default function Badge(props: BadgeProps): JSX.Element {
  const {
    anchorOrigin = { vertical: "top", horizontal: "right" },
    badgeContent,
    children,
    className = "",
    color = "default",
    invisible: invisibleProp = false,
    max = 99,
    overlap = "rectangular",
    showZero = false,
    variant = "standard",
    ...other
  } = props;

  // Lógica de exibição do conteúdo com base no valor máximo
  let displayValue = badgeContent;

  if (variant !== "dot" && typeof badgeContent === "number" && badgeContent > max) {
    displayValue = `${max}+`;
  }

  // Determina se o badge deve estar invisível
  const isZero = badgeContent === 0;
  const invisible = invisibleProp || (isZero && !showZero && variant !== "dot");

  // --- Mapeamento de Cores ---
  const colorStyles = {
    primary: "bg-blue-500 text-white",
    secondary: "bg-purple-500 text-white",
    default: "bg-gray-300 text-gray-800",
    error: "bg-red-500 text-white",
  };

  // --- Mapeamento de Posições e Overlap ---
  // A sobreposição circular empurra o badge um pouco para dentro (14%) para ficar na borda de um avatar redondo
  const isCircular = overlap === "circle" || overlap === "circular";

  const originStyles = {
    top: {
      right: isCircular
        ? "top-[14%] right-[14%] translate-x-1/2 -translate-y-1/2"
        : "top-0 right-0 translate-x-1/2 -translate-y-1/2",
      left: isCircular
        ? "top-[14%] left-[14%] -translate-x-1/2 -translate-y-1/2"
        : "top-0 left-0 -translate-x-1/2 -translate-y-1/2",
    },
    bottom: {
      right: isCircular
        ? "bottom-[14%] right-[14%] translate-x-1/2 translate-y-1/2"
        : "bottom-0 right-0 translate-x-1/2 translate-y-1/2",
      left: isCircular
        ? "bottom-[14%] left-[14%] -translate-x-1/2 translate-y-1/2"
        : "bottom-0 left-0 -translate-x-1/2 translate-y-1/2",
    },
  };

  const positionClass = originStyles[anchorOrigin.vertical][anchorOrigin.horizontal];

  // --- Mapeamento de Variante (Standard vs Dot) ---
  const variantStyles =
    variant === "dot"
      ? "w-2 h-2 min-w-[8px] rounded-full p-0"
      : "min-w-[20px] h-5 px-1.5 text-xs font-medium rounded-full";

  // Classes para a transição de visibilidade (usando scale do Tailwind)
  const visibilityClass = invisible ? "scale-0 opacity-0" : "scale-100 opacity-100";

  return (
    <span
      className="relative inline-flex align-middle shrink-0"
      {...other}
    >
      {/* Elemento Filho (ex: Ícone ou Avatar) */}
      {children}

      {/* O Badge em si */}
      <span
        className={`absolute flex flex-wrap place-content-center items-center box-border transition-transform duration-200 z-10 origin-center ${positionClass} ${colorStyles[color]} ${variantStyles} ${visibilityClass} ${className}`}
      >
        {variant !== "dot" && displayValue}
      </span>
    </span>
  );
}